/**
 * Scheduled Reports Settings Panel
 * Configure automated report generation and delivery
 */

import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Calendar, Clock, Mail, FileText, Play, Settings,
  Users, BarChart3, Shield, Loader2, Plus, X, Download,
  CheckCircle, AlertCircle, History, UserSquare2
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const REPORT_ICONS = {
  daily_census: Calendar,
  weekly_compliance: Shield,
  monthly_5_percent: BarChart3,
  patient_records: UserSquare2,
  patient_summary: FileText
};

const FREQUENCY_LABELS = {
  daily: "Daily",
  weekly: "Weekly",
  monthly: "Monthly",
  once: "On-Demand"
};

const DAY_LABELS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const ScheduledReportsPanel = ({ isDark = false }) => {
  const [schedules, setSchedules] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [editDialog, setEditDialog] = useState(false);
  const [recipientInput, setRecipientInput] = useState("");
  const [activeTab, setActiveTab] = useState("schedules");

  // Fetch schedules
  const fetchSchedules = async () => {
    try {
      const token = localStorage.getItem("hospice_token");
      const res = await fetch(`${API_URL}/api/scheduled-reports/schedules`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSchedules(data.schedules || []);
      }
    } catch (error) {
      console.error("Error fetching schedules:", error);
      toast.error("Failed to load schedules");
    } finally {
      setLoading(false);
    }
  };

  // Fetch history
  const fetchHistory = async () => {
    try {
      const token = localStorage.getItem("hospice_token");
      const res = await fetch(`${API_URL}/api/scheduled-reports/history`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setHistory(data.history || []);
      }
    } catch (error) {
      console.error("Error fetching history:", error);
    }
  };

  useEffect(() => {
    fetchSchedules();
    fetchHistory();
  }, []);

  // Toggle schedule
  const handleToggle = async (scheduleId, currentEnabled) => {
    try {
      const token = localStorage.getItem("hospice_token");
      const res = await fetch(`${API_URL}/api/scheduled-reports/schedules/${scheduleId}/toggle`, {
        method: "POST",
        headers: { "Authorization": `Bearer ${token}` }
      });
      if (res.ok) {
        setSchedules(prev => prev.map(s => 
          s.id === scheduleId ? { ...s, enabled: !currentEnabled } : s
        ));
        toast.success(`Schedule ${!currentEnabled ? 'enabled' : 'disabled'}`);
      }
    } catch (error) {
      console.error("Error toggling schedule:", error);
      toast.error("Failed to update schedule");
    }
  };

  // Run report now
  const handleRunNow = async (scheduleId, name) => {
    try {
      const token = localStorage.getItem("hospice_token");
      const res = await fetch(`${API_URL}/api/scheduled-reports/schedules/${scheduleId}/run-now`, {
        method: "POST",
        headers: { "Authorization": `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success(`${name} queued for generation`);
        fetchHistory();
      }
    } catch (error) {
      console.error("Error running report:", error);
      toast.error("Failed to run report");
    }
  };

  // Add recipient
  const handleAddRecipient = async () => {
    if (!recipientInput || !selectedSchedule) return;
    
    try {
      const token = localStorage.getItem("hospice_token");
      const res = await fetch(`${API_URL}/api/scheduled-reports/recipients?schedule_id=${selectedSchedule.id}&email=${recipientInput}`, {
        method: "POST",
        headers: { "Authorization": `Bearer ${token}` }
      });
      if (res.ok) {
        setSchedules(prev => prev.map(s => 
          s.id === selectedSchedule.id 
            ? { ...s, recipients: [...(s.recipients || []), recipientInput] }
            : s
        ));
        setSelectedSchedule(prev => ({
          ...prev,
          recipients: [...(prev.recipients || []), recipientInput]
        }));
        setRecipientInput("");
        toast.success("Recipient added");
      }
    } catch (error) {
      console.error("Error adding recipient:", error);
      toast.error("Failed to add recipient");
    }
  };

  // Remove recipient
  const handleRemoveRecipient = async (email) => {
    if (!selectedSchedule) return;
    
    try {
      const token = localStorage.getItem("hospice_token");
      await fetch(`${API_URL}/api/scheduled-reports/recipients?schedule_id=${selectedSchedule.id}&email=${email}`, {
        method: "DELETE",
        headers: { "Authorization": `Bearer ${token}` }
      });
      
      setSchedules(prev => prev.map(s => 
        s.id === selectedSchedule.id 
          ? { ...s, recipients: (s.recipients || []).filter(r => r !== email) }
          : s
      ));
      setSelectedSchedule(prev => ({
        ...prev,
        recipients: (prev.recipients || []).filter(r => r !== email)
      }));
      toast.success("Recipient removed");
    } catch (error) {
      console.error("Error removing recipient:", error);
    }
  };

  const formatTime = (hour, minute) => {
    const h = hour % 12 || 12;
    const ampm = hour >= 12 ? 'PM' : 'AM';
    return `${h}:${String(minute).padStart(2, '0')} ${ampm}`;
  };

  const getScheduleDescription = (schedule) => {
    const time = formatTime(schedule.hour || 6, schedule.minute || 0);
    if (schedule.frequency === 'daily') {
      return `Daily at ${time}`;
    } else if (schedule.frequency === 'weekly') {
      return `${DAY_LABELS[schedule.day_of_week || 0]}s at ${time}`;
    } else if (schedule.frequency === 'monthly') {
      return `${schedule.day_of_month || 1}st of each month at ${time}`;
    }
    return time;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-teal-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Calendar className="w-6 h-6 text-teal-500" />
            Scheduled Reports
          </h2>
          <p className={`text-sm mt-1 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
            Configure automated report generation and email delivery
          </p>
        </div>
        
        <div className="flex gap-2">
          <Button
            variant={activeTab === "schedules" ? "default" : "outline"}
            size="sm"
            onClick={() => setActiveTab("schedules")}
          >
            <Settings className="w-4 h-4 mr-2" />
            Schedules
          </Button>
          <Button
            variant={activeTab === "export" ? "default" : "outline"}
            size="sm"
            onClick={() => setActiveTab("export")}
          >
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
          <Button
            variant={activeTab === "history" ? "default" : "outline"}
            size="sm"
            onClick={() => setActiveTab("history")}
          >
            <History className="w-4 h-4 mr-2" />
            History
          </Button>
        </div>
      </div>

      {activeTab === "schedules" ? (
        <div className="grid gap-4">
          {schedules.map((schedule) => {
            const Icon = REPORT_ICONS[schedule.report_type] || FileText;
            
            return (
              <Card 
                key={schedule.id} 
                className={`${isDark ? "bg-slate-900 border-slate-800" : "bg-white"} ${!schedule.enabled ? "opacity-60" : ""}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${schedule.enabled ? "bg-teal-500/20" : "bg-gray-500/20"}`}>
                        <Icon className={`w-6 h-6 ${schedule.enabled ? "text-teal-500" : "text-gray-500"}`} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium">{schedule.name}</p>
                          <Badge variant="outline" className="text-xs">
                            {FREQUENCY_LABELS[schedule.frequency]}
                          </Badge>
                        </div>
                        <p className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                          {schedule.description}
                        </p>
                        <div className="flex items-center gap-4 mt-2 text-sm">
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {getScheduleDescription(schedule)}
                          </span>
                          <span className="flex items-center gap-1">
                            <Mail className="w-3 h-3" />
                            {schedule.recipients?.length || 0} recipients
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <Switch
                        checked={schedule.enabled}
                        onCheckedChange={() => handleToggle(schedule.id, schedule.enabled)}
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSelectedSchedule(schedule);
                          setEditDialog(true);
                        }}
                      >
                        <Settings className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleRunNow(schedule.id, schedule.name)}
                        disabled={!schedule.enabled}
                      >
                        <Play className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : activeTab === "export" ? (
        <ExportSection isDark={isDark} />
      ) : (
        <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"}>
          <CardHeader>
            <CardTitle className="text-lg">Report History</CardTitle>
            <CardDescription>Previously generated reports</CardDescription>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[400px]">
              <div className="space-y-3">
                {history.map((report) => (
                  <div 
                    key={report.id}
                    className={`p-3 rounded-lg flex items-center justify-between ${isDark ? "bg-slate-800" : "bg-gray-50"}`}
                  >
                    <div className="flex items-center gap-3">
                      {report.status === "completed" ? (
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      ) : report.status === "failed" ? (
                        <AlertCircle className="w-5 h-5 text-red-500" />
                      ) : (
                        <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />
                      )}
                      <div>
                        <p className="font-medium">{report.name}</p>
                        <p className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                          {new Date(report.generated_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {report.file_size && (
                        <Badge variant="outline">{report.file_size}</Badge>
                      )}
                      <Button variant="ghost" size="sm" disabled>
                        <Download className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
                {history.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    No reports generated yet
                  </div>
                )}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      )}

      {/* Edit Schedule Dialog */}
      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Configure Schedule</DialogTitle>
            <DialogDescription>
              {selectedSchedule?.name}
            </DialogDescription>
          </DialogHeader>
          
          {selectedSchedule && (
            <div className="space-y-4">
              {/* Time Settings */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Hour</Label>
                  <Select defaultValue={String(selectedSchedule.hour || 6)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 24 }, (_, i) => (
                        <SelectItem key={i} value={String(i)}>
                          {i === 0 ? '12 AM' : i < 12 ? `${i} AM` : i === 12 ? '12 PM' : `${i - 12} PM`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Minute</Label>
                  <Select defaultValue={String(selectedSchedule.minute || 0)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">:00</SelectItem>
                      <SelectItem value="15">:15</SelectItem>
                      <SelectItem value="30">:30</SelectItem>
                      <SelectItem value="45">:45</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Recipients */}
              <div>
                <Label>Email Recipients</Label>
                <div className="flex gap-2 mt-2">
                  <Input
                    type="email"
                    placeholder="email@example.com"
                    value={recipientInput}
                    onChange={(e) => setRecipientInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddRecipient()}
                  />
                  <Button onClick={handleAddRecipient} size="sm">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                
                <div className="flex flex-wrap gap-2 mt-3">
                  {(selectedSchedule.recipients || []).map((email) => (
                    <Badge key={email} variant="secondary" className="flex items-center gap-1">
                      {email}
                      <X 
                        className="w-3 h-3 cursor-pointer" 
                        onClick={() => handleRemoveRecipient(email)}
                      />
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Export Section Component
const ExportSection = ({ isDark }) => {
  const [exporting, setExporting] = useState(null);
  const [patientStatus, setPatientStatus] = useState("active");
  const [selectedSections, setSelectedSections] = useState({
    demographics: true,
    medications: true,
    care_plan: true,
    assessments: true,
    vitals: false
  });

  const handleExport = async (reportType) => {
    setExporting(reportType);
    try {
      const token = localStorage.getItem("hospice_token");
      let url = "";
      
      switch (reportType) {
        case "daily_census":
          url = `${API_URL}/api/scheduled-reports/export/daily-census`;
          break;
        case "weekly_compliance":
          url = `${API_URL}/api/scheduled-reports/export/weekly-compliance`;
          break;
        case "5_percent":
          url = `${API_URL}/api/scheduled-reports/export/5-percent-report`;
          break;
        default:
          throw new Error("Unknown report type");
      }
      
      const response = await fetch(url, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = `${reportType}_${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(downloadUrl);
        document.body.removeChild(a);
        toast.success("Report downloaded successfully");
      } else {
        throw new Error("Failed to generate report");
      }
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export report");
    } finally {
      setExporting(null);
    }
  };

  const handlePatientExport = async () => {
    setExporting("patient_records");
    try {
      const token = localStorage.getItem("hospice_token");
      const sections = Object.entries(selectedSections)
        .filter(([_, v]) => v)
        .map(([k]) => k);
      
      const response = await fetch(`${API_URL}/api/scheduled-reports/export/patient-records`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          export_all: true,
          status_filter: patientStatus,
          include_sections: sections,
          format: "pdf"
        })
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = `patient_records_${patientStatus}_${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(downloadUrl);
        document.body.removeChild(a);
        toast.success("Patient records exported successfully");
      } else {
        throw new Error("Failed to export");
      }
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export patient records");
    } finally {
      setExporting(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Quick Export */}
      <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Download className="w-5 h-5 text-teal-500" />
            Quick Export
          </CardTitle>
          <CardDescription>
            Generate and download reports instantly
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Button
              variant="outline"
              className="h-auto p-4 flex flex-col items-center gap-2"
              onClick={() => handleExport("daily_census")}
              disabled={exporting !== null}
            >
              {exporting === "daily_census" ? (
                <Loader2 className="w-6 h-6 animate-spin" />
              ) : (
                <Calendar className="w-6 h-6 text-blue-500" />
              )}
              <span className="font-medium">Daily Census</span>
              <span className="text-xs text-gray-500">Current census snapshot</span>
            </Button>
            
            <Button
              variant="outline"
              className="h-auto p-4 flex flex-col items-center gap-2"
              onClick={() => handleExport("weekly_compliance")}
              disabled={exporting !== null}
            >
              {exporting === "weekly_compliance" ? (
                <Loader2 className="w-6 h-6 animate-spin" />
              ) : (
                <Shield className="w-6 h-6 text-green-500" />
              )}
              <span className="font-medium">Compliance Summary</span>
              <span className="text-xs text-gray-500">CTI & documentation status</span>
            </Button>
            
            <Button
              variant="outline"
              className="h-auto p-4 flex flex-col items-center gap-2"
              onClick={() => handleExport("5_percent")}
              disabled={exporting !== null}
            >
              {exporting === "5_percent" ? (
                <Loader2 className="w-6 h-6 animate-spin" />
              ) : (
                <BarChart3 className="w-6 h-6 text-purple-500" />
              )}
              <span className="font-medium">5% Volunteer Report</span>
              <span className="text-xs text-gray-500">CMS requirement status</span>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Patient Records Export */}
      <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserSquare2 className="w-5 h-5 text-teal-500" />
            Patient Records Export
          </CardTitle>
          <CardDescription>
            Bulk export patient data with customizable sections
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Patient Status Filter */}
          <div className="space-y-2">
            <Label>Patient Status</Label>
            <Select value={patientStatus} onValueChange={setPatientStatus}>
              <SelectTrigger className={`w-48 ${isDark ? "bg-slate-800 border-slate-700" : ""}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active Patients</SelectItem>
                <SelectItem value="discharged">Discharged Patients</SelectItem>
                <SelectItem value="all">All Patients</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Section Selection */}
          <div className="space-y-2">
            <Label>Include Sections</Label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {[
                { id: "demographics", label: "Demographics" },
                { id: "medications", label: "Medications" },
                { id: "care_plan", label: "Care Plan" },
                { id: "assessments", label: "Assessments" },
                { id: "vitals", label: "Vital Signs" }
              ].map((section) => (
                <div
                  key={section.id}
                  className={`flex items-center space-x-2 p-2 rounded-lg border cursor-pointer ${
                    isDark ? "border-slate-700 hover:bg-slate-800" : "border-gray-200 hover:bg-gray-50"
                  }`}
                  onClick={() => setSelectedSections(prev => ({
                    ...prev,
                    [section.id]: !prev[section.id]
                  }))}
                >
                  <Checkbox
                    checked={selectedSections[section.id]}
                    onCheckedChange={() => setSelectedSections(prev => ({
                      ...prev,
                      [section.id]: !prev[section.id]
                    }))}
                  />
                  <Label className={`cursor-pointer ${isDark ? "text-gray-200" : "text-gray-700"}`}>
                    {section.label}
                  </Label>
                </div>
              ))}
            </div>
          </div>

          {/* Export Button */}
          <Button
            className="w-full bg-teal-600 hover:bg-teal-700"
            onClick={handlePatientExport}
            disabled={exporting !== null}
          >
            {exporting === "patient_records" ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Generating Export...
              </>
            ) : (
              <>
                <Download className="w-4 h-4 mr-2" />
                Export Patient Records (PDF)
              </>
            )}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default ScheduledReportsPanel;
